// ForgeTrack — AI Reports (Claude R&D Credit Narratives)
// Route: /ai-reports

import { createFileRoute } from '@tanstack/react-router';
import { useEffect, useMemo, useState } from 'react';
import { ENGINE_COLORS as C, ENGINE_FONTS as F } from '../components/engine/tokens';
import EnginePageShell, { GlassPanel, EmptyState, LoadingShimmer, StatusBadge, SectionLabel } from '../components/engine/EnginePageShell';
import {
  useProjectList,
  useAIReports,
  useAIReportMutations,
  type AIReportDetail,
} from '../lib/api/hooks-extended';

export const Route = createFileRoute('/ai-reports')({
  component: AIReportsPage,
});

function AIReportsPage() {
  const { data: projects } = useProjectList();
  const [selectedProjectId, setSelectedProjectId] = useState<number | null>(null);
  const { data: reports, loading, refetch } = useAIReports(selectedProjectId);
  const { generate, fetchDetail, loading: generating } = useAIReportMutations();
  const [viewingReport, setViewingReport] = useState<AIReportDetail | null>(null);

  const handleGenerate = async () => {
    if (!selectedProjectId) return;
    const result = await generate(selectedProjectId);
    if (result) refetch();
  };

  const handleViewReport = async (id: number) => {
    // List rows don't carry the narrative — fetch the detail when the
    // user clicks. Keeps the list payload small and avoids loading
    // every report's markdown body into memory at once.
    const detail = await fetchDetail(id);
    if (detail) setViewingReport(detail);
  };

  // Poll while any report is still generating — same pattern as /reports.
  // The job runs in Hangfire and takes 4-7 minutes; without polling the
  // user has to manually refresh to see status flip.
  const generatingCount = useMemo(
    () => reports?.filter(r => r.status === 'Generating').length ?? 0,
    [reports]
  );
  useEffect(() => {
    if (generatingCount === 0) return;
    const id = window.setInterval(() => { refetch(); }, 5000);
    return () => window.clearInterval(id);
  }, [generatingCount, refetch]);

  return (
    <EnginePageShell title="AI Reports" icon="🤖" accentColor={C.cyan}>
      {/* Project selector + Generate button */}
      <div style={{ marginBottom: 20, display: 'flex', alignItems: 'center', gap: 12 }}>
        <SectionLabel color={C.cyan}>Project</SectionLabel>
        <select
          value={selectedProjectId ?? ''}
          onChange={(e) => { setSelectedProjectId(e.target.value ? Number(e.target.value) : null); setViewingReport(null); }}
          style={{
            padding: '8px 14px', borderRadius: 8, fontSize: 13,
            background: C.panel, color: C.text, border: `1px solid ${C.faint}`,
            fontFamily: F.display, outline: 'none', minWidth: 240,
          }}
        >
          <option value="">Select a project...</option>
          {projects?.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
        {selectedProjectId && (
          <button
            onClick={handleGenerate}
            disabled={generating}
            style={{
              padding: '8px 18px', borderRadius: 8, fontSize: 12,
              fontWeight: 600, fontFamily: F.mono, cursor: 'pointer',
              background: generating ? C.faint : `linear-gradient(135deg, ${C.cyan}, ${C.neon})`,
              color: generating ? C.dim : C.bg, border: 'none',
            }}
          >
            {generating ? '⏳ Generating...' : '🤖 Generate R&D Narrative'}
          </button>
        )}
      </div>

      {!selectedProjectId && (
        <EmptyState icon="🤖" title="Select a project" subtitle="Choose a project to generate AI-powered R&D credit narratives using Claude." />
      )}

      {selectedProjectId && loading && <LoadingShimmer />}

      {/* Report viewer */}
      {viewingReport && (
        <GlassPanel accent={C.cyan} style={{ padding: 20, marginBottom: 20 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <div>
              <div style={{ fontSize: 16, fontWeight: 600, color: C.text, fontFamily: F.display }}>
                {viewingReport.reportType} Report
              </div>
              <div style={{ fontSize: 11, color: C.muted }}>
                Generated {new Date(viewingReport.generatedDate).toLocaleString()}
                {' · '}{(viewingReport.tokensIn ?? 0) + (viewingReport.tokensOut ?? 0)} tokens
                {' · '}${(viewingReport.costUSD ?? 0).toFixed(4)}
              </div>
            </div>
            <button
              onClick={() => setViewingReport(null)}
              style={{
                padding: '5px 12px', borderRadius: 6, fontSize: 11,
                background: 'none', color: C.muted, border: `1px solid ${C.faint}`, cursor: 'pointer',
              }}
            >Close</button>
          </div>
          <div style={{
            whiteSpace: 'pre-wrap', fontSize: 13, lineHeight: 1.7,
            color: C.textSoft, fontFamily: F.display,
            maxHeight: 500, overflowY: 'auto',
            padding: 16, background: C.bg, borderRadius: 8, border: `1px solid ${C.faint}`,
          }}>
            {viewingReport.narrative || '(No content)'}
          </div>
        </GlassPanel>
      )}

      {/* Report list */}
      {selectedProjectId && !loading && reports && (
        <>
          {reports.length === 0 && !viewingReport ? (
            <EmptyState
              icon="🤖"
              title="No reports yet"
              subtitle="Click 'Generate R&D Narrative' to create an AI-powered report for this project."
            />
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <SectionLabel color={C.dim}>Report History</SectionLabel>
              {reports.map((report) => (
                <GlassPanel
                  key={report.id}
                  accent={report.status === 'Ready' ? C.neon : report.status === 'Failed' ? C.coral : C.amber}
                  style={{ padding: 14, cursor: report.status === 'Ready' ? 'pointer' : 'default' }}
                  onClick={() => { if (report.status === 'Ready') handleViewReport(report.id); }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 600, color: C.text, fontFamily: F.display }}>
                        {report.title || report.reportType}
                      </div>
                      <div style={{ fontSize: 11, color: C.muted }}>
                        {new Date(report.generatedDate).toLocaleString()}
                      </div>
                    </div>
                    <StatusBadge
                      color={report.status === 'Ready' ? C.neon : report.status === 'Failed' ? C.coral : C.amber}
                      label={report.status}
                    />
                  </div>
                </GlassPanel>
              ))}
            </div>
          )}
        </>
      )}
    </EnginePageShell>
  );
}
